import { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import React, { useEffect } from 'react';

import { SlashCommand } from 'src/components/tiptap/custom-extensions/SlashCommand';
import { SlashMenuRenderer } from 'src/components/tiptap/custom-extensions/SlashMenuRenderer';
import { EditorMenu } from 'src/components/tiptap/editor-menu/EditorMenu';
import { RichTextStyles } from 'src/components/tiptap/RichTextStyles';
import { SlashMenu } from 'src/components/tiptap/slash-menu/SlashMenu';
import { getSlashMenuSuggestionItems } from 'src/features/notes/NoteModal/utils';

interface Props {
  description: string;
  onContentChange: (editor: Editor) => void;
  onUpdateTipTapEditor?: (edited: Editor | null) => void;
  className?: string;
}

export const BillingRichTextEditor: React.FC<Props> = ({
  description,
  onContentChange,
  onUpdateTipTapEditor,
  className,
}) => {
  const tiptapEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Placeholder.configure({
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:float-left before:text-[#9B9FA3] before:h-0 before:pointer-events-none',
        placeholder: '',
      }),
      Underline,
      Link,
      ListKeymap,
      SlashCommand.configure({
        suggestion: {
          items: getSlashMenuSuggestionItems,
          render: () =>
            SlashMenuRenderer({
              Component: SlashMenu,
              tippyProps: {
                placement: 'bottom-start',
              },
            }),
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: clsx(className, 'flex flex-col gap-1 mt-1 min-h-[64px]'),
      },
    },
    content: description,
    onCreate: ({ editor }) => onContentChange(editor),
    onUpdate: ({ editor }) => onContentChange(editor),
  });

  useEffect(() => {
    onUpdateTipTapEditor?.(tiptapEditor);
  }, [onUpdateTipTapEditor, tiptapEditor]);

  return (
    <div
      className={clsx(
        'box-border border border-solid rounded px-3.5 py-3 max-h-64 overflow-auto [&>div>div]:!z-[1300]',
        {
          'border-black-heading': tiptapEditor?.isFocused,
          'border-non-hover-border': !tiptapEditor?.isFocused,
        },
      )}
    >
      {tiptapEditor && (
        <>
          <BubbleMenu
            editor={tiptapEditor}
            tippyOptions={{ duration: 100, maxWidth: 'none' }}
          >
            <EditorMenu
              editor={tiptapEditor}
              textMenuActions={['heading2', 'heading3', 'text']}
            />
          </BubbleMenu>
        </>
      )}
      <RichTextStyles>
        <EditorContent editor={tiptapEditor} />
      </RichTextStyles>
    </div>
  );
};
